/**
 * Co-brands a product's login shell for a reseller: a few colour tokens, the
 * page title, and (via the store) the panel logo. The product's own shell,
 * copy and art stay; page, form and background tokens are never touched, so
 * dark mode keeps working.
 *
 * SPECIFICITY: the tokens go in one unlayered `<style>`, every rule scoped
 * `[data-ul-brand…][data-ul-reseller]` (0,2,0 or more). Every brand declares
 * its tokens under `[data-ul-brand]` or `[data-ul-brand="<product>"]`
 * (0,1,0), light and inside its `prefers-color-scheme: dark` block alike — a
 * media query adds no specificity — so a reseller value wins wherever one is
 * emitted, whatever the stylesheet order. Nothing sets these tokens inline on
 * <html> (theme-aware products keep colours out of `ClientTheme.variables`).
 *
 * DARK MODE: the panel is dark in both schemes, so its tokens apply in both.
 * The form tokens (button, focus, link hover) are split by scheme, because a
 * reseller's primary that reads on a light form can vanish on a dark one:
 *   - light scheme: the primary, for every shell;
 *   - dark scheme, a shell whose form stays light (no DARK_FORM_BG entry,
 *     e.g. GuestMaker): the primary;
 *   - dark scheme, a shell with a dark form: the first of primary and accent
 *     with at least 3:1 against that form, else nothing at all, so the
 *     shell's own dark tokens stay.
 */

import {
  type AuthorizationParams,
  fetchResellerBranding,
  isHexColor,
  readLoginHost,
  type ResellerBranding,
} from "@/utils/reseller/loginBranding";
import {
  getResellerBranding,
  setResellerBranding,
} from "@/utils/reseller/store";
import type { Brand } from "@/utils/theme/brand";

export const RESELLER_ATTRIBUTE = "data-ul-reseller";
export const RESELLER_STYLE_ID = "ul-reseller-tokens";

/**
 * The dark-mode form background of each shell whose form follows the system
 * theme. Mirrors the `--bs-form-bg` in each product's dark block; a test reads
 * the CSS to keep them in step.
 */
export const DARK_FORM_BG: Readonly<Partial<Record<Brand, string>>> = {
  wifibot: "#0d151b", // src/brands/wifibot/wifibot.css:49
  admin: "#121212", // src/brands/admin/admin.css:49
};

/** WCAG 2's minimum for UI components against their background. */
const MIN_DARK_CONTRAST = 3;

const LIGHT_LABEL = "#ffffff";
const DARK_LABEL = "#0f172a";

type Rgb = [number, number, number];

function toRgb(hex: string): Rgb {
  const value = parseInt(hex.slice(1), 16);
  return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
}

function toHex(rgb: Rgb): string {
  return `#${rgb.map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}

/** Moves each channel `amount` of the way to black. */
function darken(hex: string, amount: number): string {
  const rgb = toRgb(hex).map((c) => Math.round(c * (1 - amount))) as Rgb;
  return toHex(rgb);
}

function withAlpha(hex: string, alpha: number): string {
  const [r, g, b] = toRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** WCAG 2 relative luminance. */
function luminance(hex: string): number {
  const [r, g, b] = toRgb(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrast(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/** Whichever button label contrasts more with the button colour. */
export function buttonLabelColor(hex: string): "#ffffff" | "#0f172a" {
  const bg = luminance(hex);
  return contrast(bg, luminance(LIGHT_LABEL)) >=
    contrast(bg, luminance(DARK_LABEL))
    ? LIGHT_LABEL
    : DARK_LABEL;
}

function rule(
  selector: string,
  tokens: Record<string, string>,
  indent = ""
): string {
  const body = Object.entries(tokens)
    .map(([name, value]) => `${indent}  ${name}: ${value};`)
    .join("\n");
  return `${indent}${selector} {\n${body}\n${indent}}`;
}

function media(query: string, rules: string[]): string {
  return `@media ${query} {\n${rules.join("\n")}\n}`;
}

/** The form's button, focus and link-hover tokens in one colour. */
function formTokens(color: string): Record<string, string> {
  return {
    "--bs-button-bg": color,
    "--bs-button-bg-hover": darken(color, 0.1),
    "--bs-button-label": buttonLabelColor(color),
    "--bs-focus-border": color,
    "--bs-focus-ring": withAlpha(color, 0.2),
    "--bs-button-glow": withAlpha(color, 0.35),
    "--bs-button-ring": withAlpha(color, 0.35),
    "--bs-link-hover": color,
  };
}

/** The first colour with at least 3:1 against `background`, if any. */
function readableOn(
  background: string,
  colors: readonly string[]
): string | undefined {
  const bg = luminance(background);
  return colors.find((c) => contrast(luminance(c), bg) >= MIN_DARK_CONTRAST);
}

/**
 * The reseller's colour tokens as CSS. Built only from validated `#rrggbb`
 * colours, never from the display name or URLs. Without a primary colour
 * there are no tokens; without an accent the panel uses the primary.
 */
export function resellerTokenCss(b: ResellerBranding): string {
  if (!isHexColor(b.colorPrimary)) return "";
  const primary = b.colorPrimary.toLowerCase();
  const accent = isHexColor(b.colorAccent)
    ? b.colorAccent.toLowerCase()
    : primary;

  const reseller = `[${RESELLER_ATTRIBUTE}]`;
  const darkForms = Object.entries(DARK_FORM_BG);
  const lightForms = `[data-ul-brand]:not(${darkForms
    .map(([brand]) => `[data-ul-brand="${brand}"]`)
    .join(", ")})${reseller}`;
  // Accent only when it is a colour of its own: the primary is tried first.
  const candidates = isHexColor(b.colorAccent) ? [primary, accent] : [primary];

  return [
    rule(`[data-ul-brand]${reseller}`, {
      "--bs-panel-accent": accent,
      "--bs-panel-eyebrow": withAlpha(accent, 0.75),
    }),
    media("not all and (prefers-color-scheme: dark)", [
      rule(`[data-ul-brand]${reseller}`, formTokens(primary), "  "),
    ]),
    media("(prefers-color-scheme: dark)", [
      rule(lightForms, formTokens(primary), "  "),
      ...darkForms.flatMap(([brand, background]) => {
        const color = readableOn(background, candidates);
        return color
          ? [
              rule(
                `[data-ul-brand="${brand}"]${reseller}`,
                formTokens(color),
                "  "
              ),
            ]
          : [];
      }),
    ]),
    rule(`[data-ul-brand="wifibot"]${reseller}`, {
      "--wb-signal": accent,
      "--wb-signal-deep": darken(accent, 0.2),
    }),
  ].join("\n");
}

export function resellerTitle(b: ResellerBranding): string {
  return `Sign in · ${b.displayName}`;
}

/** Marks the page as a reseller login and applies its tokens and title. */
export function applyResellerBranding(b: ResellerBranding): void {
  document.documentElement.setAttribute(RESELLER_ATTRIBUTE, "");

  let style = document.getElementById(RESELLER_STYLE_ID);
  if (!(style instanceof HTMLStyleElement)) {
    style?.remove();
    style = document.createElement("style");
    style.id = RESELLER_STYLE_ID;
    document.head.appendChild(style);
  }
  style.textContent = resellerTokenCss(b);

  document.title = resellerTitle(b);
}

interface StartResellerBrandingInput {
  clientId?: string;
  authorizationParams?: AuthorizationParams;
}

/**
 * Fire-and-forget: when the login carries a valid address, look its branding
 * up and apply it once it arrives. The caller never waits, and without an
 * address (every non-reseller login) nothing is fetched at all.
 */
export function startResellerBranding({
  clientId,
  authorizationParams,
}: StartResellerBrandingInput): void {
  const host = readLoginHost(authorizationParams);
  if (!host || !clientId) return;

  fetchResellerBranding(host, clientId)
    .then((branding) => {
      // A repeat call gets the memoised lookup's same object back, so the
      // branding is applied once.
      if (!branding || branding === getResellerBranding()) return;
      applyResellerBranding(branding);
      setResellerBranding(branding);
    })
    .catch(() => {
      // Co-branding is cosmetic: any failure leaves the product's shell.
    });
}
